import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="popup"
export default class extends Controller {
  static values = {
    closeOnBackdropClick: { type: Boolean, default: true },
    closeOnEsc: { type: Boolean, default: true }
  }

  static targets = ["modal", "backdrop"]

  connect() {
    this.initialized = false
    // Do not auto-initialize when visible: only initialize when explicitly opened (e.g. by user_menu).
    // Also heal stale body scroll locks when this popup is hidden and no other popup is visible.
    if (this.element.classList.contains("hidden") && !this.hasVisiblePopup()) {
      document.body.style.overflow = ""
    }
  }

  initialize() {
    // Prevent double initialization
    if (this.initialized) {
      return
    }
    
    this.initialized = true
    
    // Store the element that had focus before opening
    this.previousActiveElement = document.activeElement
    
    // Lock body scroll
    document.body.style.overflow = "hidden"
    
    // Trap focus inside modal
    this.trapFocus()
    
    // Add ESC key listener
    if (this.closeOnEscValue) {
      this.boundHandleEsc = this.handleEsc.bind(this)
      document.addEventListener("keydown", this.boundHandleEsc)
    }
    
    // Focus first focusable element in modal
    this.focusFirstElement()
  }

  disconnect() {
    this.cleanup()
  }

  close() {
    this.element.classList.add("hidden")
    // Clean up event listeners and restore body scroll
    this.cleanup()
  }

  cleanup() {
    this.initialized = false
    
    // Restore body scroll only when no popup remains visible.
    if (!this.hasVisiblePopup()) {
      document.body.style.overflow = ""
    }
    
    // Remove ESC key listener
    if (this.boundHandleEsc) {
      document.removeEventListener("keydown", this.boundHandleEsc)
      this.boundHandleEsc = null
    }
    
    // Remove focus trap listener
    if (this.boundHandleTab) {
      this.element.removeEventListener("keydown", this.boundHandleTab)
      this.boundHandleTab = null
    }
    
    // Return focus to previous element
    if (this.previousActiveElement && this.previousActiveElement.focus) {
      this.previousActiveElement.focus()
    }
  }

  closeBackdrop(event) {
    // Only close if clicking directly on the backdrop (not on modal or its children)
    if (this.closeOnBackdropClickValue) {
      const modal = this.element.querySelector('[role="dialog"]')
      if (modal && !modal.contains(event.target)) {
        this.close()
      }
    }
  }

  handleEsc(event) {
    if (this.closeOnEscValue && event.key === "Escape") {
      event.preventDefault()
      this.close()
    }
  }

  trapFocus() {
    // Get all focusable elements within the modal
    const focusableElements = this.element.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
    
    if (focusableElements.length === 0) return
    
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]
    
    // Handle Tab key to trap focus
    this.boundHandleTab = (event) => {
      if (event.key !== "Tab") return
      
      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault()
          lastElement.focus()
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault()
          firstElement.focus()
        }
      }
    }
    
    this.element.addEventListener("keydown", this.boundHandleTab)
  }

  focusFirstElement() {
    const focusableElements = this.element.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
    
    if (focusableElements.length > 0) {
      // Focus the close button or first focusable element
      const closeButton = this.element.querySelector('button[aria-label="Close"]')
      if (closeButton) {
        closeButton.focus()
      } else {
        focusableElements[0].focus()
      }
    }
  }

  hasVisiblePopup() {
    return Boolean(document.querySelector('[data-controller~="popup"]:not(.hidden)'))
  }
}
